import { Link } from 'react-router-dom';
import { useCompanyInfo } from '../lib/cms';
import { imageUrl } from '../lib/images';

export default function Footer() {
  const companyInfo = useCompanyInfo();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#F3F4F7] text-[#4B515D] pt-20 pb-14 border-t border-[#D6D3CE]/60">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Quality and trust principles bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pb-14 mb-14 border-b border-[#D6D3CE]/60">
          <div className="flex items-start gap-4 p-6 bg-white border border-[#D6D3CE]/40">
            <div className="w-10 h-10 bg-[#004FA4]/10 text-[#004FA4] flex items-center justify-center shrink-0 text-lg">
              <i className="fa-sharp fa-solid fa-award" aria-hidden="true"></i>
            </div>
            <div>
              <h4 className="font-medium text-[#143463] text-base mb-1">Kvalitetshåndværk</h4>
              <p className="text-xs text-[#4B515D] leading-relaxed">
                Gennemarbejdede løsninger produceret og monteret med fokus på holdbarhed og præcision.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-6 bg-white border border-[#D6D3CE]/40">
            <div className="w-10 h-10 bg-[#004FA4]/10 text-[#004FA4] flex items-center justify-center shrink-0 text-lg">
              <i className="fa-sharp fa-solid fa-location-dot" aria-hidden="true"></i>
            </div>
            <div>
              <h4 className="font-medium text-[#143463] text-base mb-1">Landsdækkende montage</h4>
              <p className="text-xs text-[#4B515D] leading-relaxed">
                Vi leverer og monterer skræddersyede løsninger til kunder i hele Danmark.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-6 bg-white border border-[#D6D3CE]/40">
            <div className="w-10 h-10 bg-[#004FA4]/10 text-[#004FA4] flex items-center justify-center shrink-0 text-lg">
              <i className="fa-sharp fa-solid fa-shield-check" aria-hidden="true"></i>
            </div>
            <div>
              <h4 className="font-medium text-[#143463] text-base mb-1">Troværdig dialog</h4>
              <p className="text-xs text-[#4B515D] leading-relaxed">
                {companyInfo.slogan || "Vi bygger tillid og leverer resultater"} – fra første opmåling til færdig montage.
              </p>
            </div>
          </div>
        </div>

        {/* Main Footer Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 pb-14">
          {/* Brand Col */}
          <div className="lg:col-span-2 space-y-4">
            <Link to="/" className="inline-block" aria-label="HOVENDALS ApS Forside">
              <img
                src={imageUrl(companyInfo.logo, 'width=380,quality=90,format=auto')}
                alt="HOVENDALS ApS"
                className="h-11 w-auto object-contain"
              />
            </Link>

            <p className="text-xs font-medium uppercase tracking-wider text-[#004FA4]">
              {companyInfo.slogan}
            </p>

            <p className="text-sm text-[#4B515D] leading-relaxed max-w-md font-light">
              Hovendals er din professionelle partner inden for hegn, låger, porte, terrasser, carporte og skure. 
              Vi leverer skræddersyede kvalitetsløsninger til både private og erhverv i hele Danmark.
            </p>

            <div className="flex items-center gap-3 pt-2">
              <a
                href={companyInfo.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Følg Hovendals på Facebook"
                className="w-9 h-9 bg-white border border-[#D6D3CE] text-[#143463] hover:text-[#004FA4] hover:border-[#004FA4] flex items-center justify-center transition-colors text-sm"
              >
                <i className="fa-brands fa-facebook-f" aria-hidden="true"></i>
              </a>
              <a
                href={`tel:${companyInfo.phoneFormatted}`}
                aria-label="Ring til Hovendals"
                className="w-9 h-9 bg-white border border-[#D6D3CE] text-[#143463] hover:text-[#004FA4] hover:border-[#004FA4] flex items-center justify-center transition-colors text-sm"
              >
                <i className="fa-sharp fa-solid fa-phone" aria-hidden="true"></i>
              </a>
              <a
                href={`mailto:${companyInfo.email}`}
                aria-label="Send e-mail til Hovendals"
                className="w-9 h-9 bg-white border border-[#D6D3CE] text-[#143463] hover:text-[#004FA4] hover:border-[#004FA4] flex items-center justify-center transition-colors text-sm"
              >
                <i className="fa-sharp fa-solid fa-envelope" aria-hidden="true"></i>
              </a>
            </div>
          </div>

          {/* Col: Hegn & Låger */}
          <div>
            <h4 className="text-xs font-semibold tracking-wider text-[#143463] uppercase mb-4">
              Hegn & Låger
            </h4>
            <ul className="space-y-2 text-sm font-light">
              <li>
                <Link to="/komposithegn" className="hover:text-[#004FA4] transition-colors">
                  Komposithegn
                </Link>
              </li>
              <li>
                <Link to="/industrihegn" className="hover:text-[#004FA4] transition-colors">
                  Industrihegn
                </Link>
              </li>
              <li>
                <Link to="/stoejhegn" className="hover:text-[#004FA4] transition-colors">
                  Støjhegn
                </Link>
              </li>
              <li>
                <Link to="/panelhegn" className="hover:text-[#004FA4] transition-colors">
                  Panelhegn
                </Link>
              </li>
              <li>
                <Link to="/smedejernshegn" className="hover:text-[#004FA4] transition-colors">
                  Smedejernshegn
                </Link>
              </li>
              <li>
                <Link to="/betonhegn" className="hover:text-[#004FA4] transition-colors">
                  Betonhegn
                </Link>
              </li>
              <li>
                <Link to="/laager" className="hover:text-[#004FA4] transition-colors">
                  Låger & Kompositdøre
                </Link>
              </li>
            </ul>
          </div>

          {/* Col: Porte & Byggeri */}
          <div>
            <h4 className="text-xs font-semibold tracking-wider text-[#143463] uppercase mb-4">
              Porte & Løsninger
            </h4>
            <ul className="space-y-2 text-sm font-light">
              <li>
                <Link to="/skydeporte" className="hover:text-[#004FA4] transition-colors">
                  Skydeporte
                </Link>
              </li>
              <li>
                <Link to="/dobbeltporte" className="hover:text-[#004FA4] transition-colors font-normal text-[#004FA4]">
                  Dobbeltporte
                </Link>
              </li>
              <li>
                <Link to="/foldeporte-i-funktionelt-design" className="hover:text-[#004FA4] transition-colors">
                  Foldeporte
                </Link>
              </li>
              <li>
                <Link to="/terrasser" className="hover:text-[#004FA4] transition-colors font-normal text-[#004FA4]">
                  Terrasser
                </Link>
              </li>
              <li>
                <Link to="/traeterrasse" className="hover:text-[#004FA4] transition-colors">
                  Træterrasse
                </Link>
              </li>
              <li>
                <Link to="/kompositterrasse" className="hover:text-[#004FA4] transition-colors">
                  Kompositterrasse
                </Link>
              </li>
              <li>
                <Link to="/carport" className="hover:text-[#004FA4] transition-colors">
                  Carport & Skur
                </Link>
              </li>
              <li>
                <Link to="/facader" className="hover:text-[#004FA4] transition-colors">
                  Facader & Vinduespartier
                </Link>
              </li>
            </ul>
          </div>

          {/* Col: Kontakt information */}
          <div>
            <h4 className="text-xs font-semibold tracking-wider text-[#143463] uppercase mb-4">
              Kontakt
            </h4>
            <div className="space-y-3 text-sm font-light">
              <p className="text-xs text-[#4B515D]">
                <strong className="text-[#143463] font-medium block">HOVENDALS ApS</strong>
                CVR: {companyInfo.cvr}
              </p>

              <div>
                <span className="text-xs text-[#143463] font-medium block">Telefon:</span>
                <a
                  href={`tel:${companyInfo.phoneFormatted}`}
                  className="hover:text-[#004FA4] transition-colors"
                >
                  {companyInfo.phone}
                </a>
              </div>

              <div>
                <span className="text-xs text-[#143463] font-medium block">E-mail:</span>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="hover:text-[#004FA4] transition-colors break-all"
                >
                  {companyInfo.email}
                </a>
              </div>

              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-block px-4 py-2 bg-[#004FA4] hover:bg-[#183E7F] text-white text-xs font-medium uppercase tracking-wider transition-colors"
                >
                  Indhent tilbud
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-[#D6D3CE]/60 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-light text-[#4B515D]">
          <p>© {currentYear} HOVENDALS ApS. Alle rettigheder forbeholdes.</p>
          <div className="flex items-center gap-6">
            <span>Vi leverer løsninger i hele Danmark</span>
            <Link to="/kontakt" className="hover:text-[#004FA4] transition-colors">
              Kontakt os
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
